import * as H from "history";
import * as _ from "lodash";
import * as Redux from "redux";

import type { IApiError } from "src/library/utils/errors/apiError";
import type { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import type { ThemeType } from "src/library/utils/theme";
import type { InstalledModuleType } from "src/utils/constants";
import type {
  ERPType,
  IAwsSetting,
  IPlatformSetting
} from "src/core/shared/api/globalSettings.api";
import type { GlobalActions } from "src/core/shared/store/global/global.actions";
import * as AT from "src/core/shared/store/global/global.constants";
import type { ContextType } from "src/core/shared/hooks/menu/useMenu";
import type { IRole } from "src/core/shared/api/roles.api";
import type { IUser } from "src/core/users/api/users.api";
import type { IOrderStatus } from "src/core/orders/api/orders.api";
import { ISupportTicketType } from "src/modules/support/api/support.api";
import { ICreditRequestReasonCode } from "src/modules/credit-request/api/credit-request.api";
import { ILeadTimeOverride } from "src/modules/lead-time-override/api/lead-time-override.api";
import { IOpenDay } from "src/modules/shutdowns/api/open-day.api";
import {
  IProduct,
  IProductRelatedGroup,
  IProductLeadTimeGroup
} from "src/core/products/api/products.api";
import {
  IDiscountType,
  PointOfContact
} from "src/core/drops/api/drops.api";
import { ProductTagType } from "src/modules/product-tags/api/product-tags.api";
import { IImportSetting } from "src/core/shared/api/importSetting.api";
import { ShutdownDateInfo } from "src/modules/shutdowns/utils/formatShutdownDates";

export type AppParentType = "DEFAULT";

export interface IGlobalContextModal {
  title: string;
  details: string;
  type: ContextType;
  origin: string;
}

export interface IAppContext<T = any> {
  type: ContextType;
  data?: T;
}

export interface FeedbackSurvey {
  admin: {
    surveyUrl: string | null;
    enabled: true;
  };
  customer: {
    surveyUrl: string | null;
    enabled: boolean;
  };
}

export interface IAppSettings {
  orderStatuses: IOrderStatus[];
  platform: IPlatformSetting;
  theme: ThemeType;
  erp: { name: ERPType };
  installedModules: InstalledModuleType[];
  terminology: ITerminology;
  aws: IAwsSetting;
  minimumLeadTime: IProductLeadTimeGroup;
  feedbackSurvey: FeedbackSurvey | null;
  timezone?: TimezoneSetting; //TODO: can remove optional on a future deploy
  shutdowns?: ShutdownDateInfo[] | null;
  openDays: IOpenDay[];
  leadTimeOverrides?: ILeadTimeOverride[];
  supportSettings?: SupportSetting;
  supportTicketTypes: ISupportTicketType[];
  // seedlessSettings?: SeedlessSetting;
  importSettings: IImportSetting[];
  payment?: {
    accountId: string;
    publicKey: string;
    canCancel?: boolean;
    preauthorizationsEnabled: boolean;
  };
  creditRequestReasonCodes?: ICreditRequestReasonCode[];
  rjlDeliveryNumbers?: IRJLDeliveryNumber[];
  displaySettings: DisplaySettings;
  pointOfContact?: PointOfContact;
  allowPendingCustomers?: boolean;
  componentLayout: ComponentLayoutSettings;
  siteSettings?: SiteSettings;
  productRelatedGroups?: IProductRelatedGroup[]
  leadTimeGroups: IProductLeadTimeGroup[];
  hasPackSizes: boolean;
  packSizeConstraints: IPackSizeConstraint[];
  discountTypes: IDiscountType[]
}

export interface IPackSizeConstraint {
  /** pack size code */
  code: string | "weight";
  /** setting max quantity */
  maxQuantity?: number;
  /** setting min quantity */
  minQuantity?: number;
  /** error level to determine validation and messaging behaviour */
  errorLevel?: string;
}

export interface SiteSettings {
  sso: {
    subdomains?: Array<{
      name: string;
      default?: boolean;
    }>;
    providers: Array<{
      type: SSOProviders,
      /** Used for custom SSO provider */
      providerName?: string,
      /** Used for custom subdomain login */
      subdomain?: string;
    }>
  };
  schedule: {
    extendLength: number;
  };
}

export enum SSOProviders {
  Microsoft = "microsoft",
  Google = "google"
}

export interface DisplaySettings {
  product: {
    defaultSort: keyof Pick<IProduct, "partNumber" | "sortOrder" | "description">
  },
  order?: {
    // displays in places where the customer is blocked for ordering due to cutoff
    cutoffMessage?: string;
    // how long after cutoff should it show the cutoffMessage for?
    hoursAfterCutOff?: number;
    // Displays on the order placed screen. Allows $DeliveryDate replacement in the string
    orderPlacedMessage?: string;
  }
}

export interface ComponentLayoutSettings {
  productCard: {
    layout: { details: ProductCardLayoutDetail[]; };
    /** number refers to how many lines to trucate */
    titleWrap?: "truncate" | "wrap" | number;
    productTagTypes?: ProductTagType[];
  };
}

/** string references custom data, and must start with "custom:", eg custom:Weight */
export type ProductCardLayoutDetail = "packSizes" | "shelfLife" | `custom:${string}`;

export interface IRJLDeliveryNumber {
  number: number;
  description?: string;
  priority: number;
  options?: {
    // numbers are isoDays where monday = 1, tuesday = 2 etc
    allowedDeliveryDays?: number[];
    maxLeadTimeGroup?: string;
    minLeadTimeGroup?: string;
  }
}

export interface SeedlessSetting {
  imports: {
    products: boolean;
    customers: boolean;
    prices: boolean;
    users: boolean;
  }
}

export interface SupportSetting {
  supportDocuments: boolean;
  supportTickets: {
    enabled: boolean;
    supportEmail: string;
  };
}

export interface TimezoneSetting {
  zone: string;
}

export interface MasqueradeMode {
  active: boolean;
  redirect?: string;
  initialRole?: IRole | null;
}

export interface LatestPackSize {
  partNumber: string;
  packSizeCode: string | null;
}

export interface IGlobalStore {
  error: IApiError | null;
  role: IRole | null;
  user: IUser | null;
  userSuspended: boolean;
  contextModal: IGlobalContextModal | null;
  settings: IAppSettings | null;
  history: Array<H.Location<{ context?: ContextType }>>;
  surveyTracking?: {
    dismissed?: Date;
    clicked?: Date;
  };
  showJobsQueue: boolean;
  masqueradeMode?: MasqueradeMode;
  latestPackSizes?: LatestPackSize[];
}

const initialState: IGlobalStore = {
  error: null,
  contextModal: null,
  user: null,
  userSuspended: false,
  role: null,
  settings: null,
  history: [],
  showJobsQueue: false,
  masqueradeMode: { active: false },
  latestPackSizes: []
};

export const global: Redux.Reducer<IGlobalStore> = (state: IGlobalStore = initialState, action: GlobalActions): IGlobalStore => {
  switch (action.type) {
    case AT.SET_GLOBAL_USER: {
      // If there is a role set in state, but no role given by the action
      // ignore the role update
      const newValue = { ...state };

      if (!action.role && state.role) {
        newValue [ "user" ] = action.user ;
      } else {
        newValue[ "user" ] = action.user;
        newValue[ "role" ] = action.role || null;
      }

      if (_.isEqual(state, newValue)) {
        return state;
      } else {
        return newValue;
      }
    }

    case AT.SET_GLOBAL_ROLE:
      return {
        ...state,
        role: action.role
      };

    case AT.SET_GLOBAL_USER_STATUS:
      return {
        ...state,
        userSuspended: action.userSuspended
      };

    case AT.SET_GLOBAL_USER_NAME:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.user
        }
      };

    case AT.SET_GLOBAL_ERROR:
      return {
        ...state,
        error: action.error
      };

    case AT.SET_GLOBAL_MODAL:
      return {
        ...state,
        contextModal: action.modalData
      };

    case AT.SET_SURVEY_TRACKING:
      return {
        ...state,
        surveyTracking: action.surveyTracking
      };

    case AT.SHOW_JOB_QUEUE:
      return {
        ...state,
        showJobsQueue: action.show
      };

    case AT.MASQUERADE_MODE:
      return {
        ...state,
        masqueradeMode: action.masqueradeMode
      };

    case AT.SET_APP_SETTINGS: {
      if (_.isEqual(state.settings, action.settings)) {
        return state;
      } else {
        return {
          ...state,
          settings: { ...action.settings }
        };
      }
    }

    case AT.PURGE_MENU:
      return {
        ...initialState,
        surveyTracking: state.surveyTracking
      }; // keep the survey tracking between login sessions

    case AT.PURGE_USER_ROLE:
      return {
        ...state,
        role: null,
        user: null
      };

    case AT.ADD_LOCATION: {
      const newHistory = state.history;
      const currentRouteIndex = _.findIndex(state.history, loc => loc.key === action.location.key);

      if (newHistory) {
        if (currentRouteIndex !== -1) {
          newHistory.splice(0, currentRouteIndex);
        } else {
          newHistory.unshift(action.location);
        }

        if (newHistory.length >= 40) {
          newHistory.splice(40);
        }

        return {
          ...state,
          history: newHistory
        };
      } else {
        return state;
      }
    }
    case AT.ADD_LATEST_PACK_SIZE: {
      const newPackSizes = state.latestPackSizes;
      const targetProduct = newPackSizes?.find(packSize => packSize.partNumber === action.latestPackSize.partNumber);

      if (targetProduct) {
        targetProduct.packSizeCode = action.latestPackSize.packSizeCode;
      } else {
        newPackSizes?.push(action.latestPackSize);
      }

      return {
        ...state,
        latestPackSizes: newPackSizes ? [ ...newPackSizes ] : []
      };
    }
    default:
      return state;
  }
};
